import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2, FileText } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ThankYouSettings } from "@/components/ThankYouSettings";
import { SecurityPanel, MfaResetButton } from "@/components/SecurityPanel";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { useAuth, usePrefs } from "@/lib/context";
import { fetcher, useCategories, catName } from "@/lib/hooks";
import { PageHeader, StatusBadge, AuthImage } from "@/components/common";

const ROLES = ["admin", "supervision", "gestionnaire", "proprietaire", "locataire", "prestataire"];

function Users({ isAdmin }) {
  const { user } = useAuth();
  const { t } = usePrefs();
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["admin-users"], queryFn: () => fetcher("/admin/users") });
  const setAccount = async (id, status) => { try { await api.put(`/admin/users/${id}/account`, { status }); qc.invalidateQueries({ queryKey: ["admin-users"] }); } catch (e) { toast.error(errMsg(e)); } };
  const setRole = async (id, role) => { try { await api.put(`/admin/users/${id}/role`, { role }); qc.invalidateQueries({ queryKey: ["admin-users"] }); toast.success(t("save")); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="divide-y divide-slate-100 rounded-sm border border-slate-200 bg-white">
      {data.map((u) => (
        <div key={u.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3" data-testid={`user-row-${u.id}`}>
          <div>
            <div className="flex items-center gap-2 font-semibold">{u.name}
              {u.account_status === "pending" && <StatusBadge value="pending" label={t("accountPending")} testId={`account-status-${u.id}`} />}
              {u.account_status === "rejected" && <StatusBadge value="rejected" label={t("rejected")} testId={`account-status-${u.id}`} />}
            </div>
            <div className="text-xs text-slate-500">{u.email}{[u.company, u.position, u.phone, u.pro_card].filter(Boolean).map((x) => ` · ${x}`).join("")}</div>
            {u.message && <div className="mt-1 text-xs italic text-slate-500">« {u.message} »</div>}
            {["admin", "supervision"].includes(u.role) && <div className={`mt-1 text-[11px] font-semibold ${u.mfa_enabled ? "text-emerald-700" : "text-amber-700"}`} data-testid={`mfa-badge-${u.id}`}>{u.mfa_enabled ? t("mfaEnabled") : t("mfaOff")}</div>}
            {isAdmin && <MfaResetButton u={u} />}
            {isAdmin && ["pending", "rejected"].includes(u.account_status) && (
              <div className="mt-2 flex gap-2">
                <Button size="sm" className="h-7 rounded-sm bg-emerald-700 text-xs hover:bg-emerald-800" onClick={() => setAccount(u.id, "active")} data-testid={`account-approve-${u.id}`}>{t("approveAccess")}</Button>
                {u.account_status === "pending" && <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs" onClick={() => setAccount(u.id, "rejected")} data-testid={`account-reject-${u.id}`}>{t("reject")}</Button>}
              </div>
            )}
          </div>
          {isAdmin && u.id !== user.id ? (
            <Select value={u.role} onValueChange={(v) => setRole(u.id, v)}>
              <SelectTrigger className="h-8 w-44 rounded-sm text-xs" data-testid={`user-role-select-${u.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{t(`role_${r}`)}</SelectItem>)}</SelectContent>
            </Select>
          ) : <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t(`role_${u.role}`)}</span>}
        </div>
      ))}
    </div>
  );
}

function Providers({ isAdmin }) {
  const { t } = usePrefs();
  const { label } = useCategories();
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["admin-providers"], queryFn: () => fetcher("/admin/providers") });
  const validate = async (id, status) => { try { await api.put(`/admin/providers/${id}/validate`, { status }); qc.invalidateQueries({ queryKey: ["admin-providers"] }); qc.invalidateQueries({ queryKey: ["directory"] }); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {data.map((p) => (
        <div key={p.id} className="rounded-sm border border-slate-200 bg-white p-5" data-testid={`provider-card-${p.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div><div className="font-display text-lg font-bold">{p.company || p.name}</div><div className="text-xs text-slate-500">{p.name} · {p.email} {p.phone && `· ${p.phone}`}</div></div>
            <StatusBadge value={p.provider_status || "pending"} label={t(p.provider_status === "pending" || !p.provider_status ? "pendingV" : p.provider_status)} testId={`provider-status-${p.id}`} />
          </div>
          <div className="mt-3 text-sm"><span className="text-slate-500">{t("trades")} :</span> {(p.trades || []).map(label).join(", ") || "—"}</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {(p.documents || []).map((d) => d.path?.endsWith(".pdf") ? <span key={d.path} className="flex items-center gap-1 rounded-sm border px-2 py-1 text-xs"><FileText size={12} />{d.name}</span> : <AuthImage key={d.path} path={d.path} className="h-14 w-16 rounded-sm object-cover" />)}
          </div>
          {isAdmin && (
            <div className="mt-4 flex gap-2">
              <Button size="sm" className="rounded-sm bg-emerald-700 hover:bg-emerald-800" disabled={p.provider_status === "validated"} onClick={() => validate(p.id, "validated")} data-testid={`provider-validate-${p.id}`}>{t("validate")}</Button>
              <Button size="sm" variant="outline" className="rounded-sm" disabled={p.provider_status === "rejected"} onClick={() => validate(p.id, "rejected")} data-testid={`provider-reject-${p.id}`}>{t("reject")}</Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function Categories({ isAdmin }) {
  const { t, lang } = usePrefs();
  const cats = useCategories();
  const qc = useQueryClient();
  const [f, setF] = useState({ type: "trade", key: "", name_fr: "", name_en: "", name_es: "", name_pt: "", name_ar: "" });
  const add = async () => { try { await api.post("/admin/categories", f); qc.invalidateQueries({ queryKey: ["categories"] }); setF({ ...f, key: "", name_fr: "", name_en: "", name_es: "", name_pt: "", name_ar: "" }); } catch (e) { toast.error(errMsg(e)); } };
  const del = async (id) => { try { await api.delete(`/admin/categories/${id}`); qc.invalidateQueries({ queryKey: ["categories"] }); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-6">
      {isAdmin && (
        <div className="grid gap-2 rounded-sm border border-slate-200 bg-white p-4 sm:grid-cols-4">
          <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
            <SelectTrigger data-testid="category-type-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="trade">{t("trades")}</SelectItem><SelectItem value="property">{t("properties")}</SelectItem></SelectContent>
          </Select>
          <Input placeholder={t("catKeyPh")} value={f.key} onChange={(e) => setF({ ...f, key: e.target.value.toLowerCase().replace(/\s+/g, "_") })} data-testid="category-key-input" />
          <Input placeholder={`${t("name")} FR`} value={f.name_fr} onChange={(e) => setF({ ...f, name_fr: e.target.value })} data-testid="category-namefr-input" />
          <Input placeholder={`${t("name")} EN`} value={f.name_en} onChange={(e) => setF({ ...f, name_en: e.target.value })} data-testid="category-nameen-input" />
          {["es", "pt", "ar"].map((l) => <Input key={l} placeholder={`${t("name")} ${l.toUpperCase()} (${t("optional")})`} value={f[`name_${l}`]} onChange={(e) => setF({ ...f, [`name_${l}`]: e.target.value })} data-testid={`category-name${l}-input`} />)}
          <Button onClick={add} disabled={!f.key || !f.name_fr || !f.name_en} className="rounded-sm bg-[#0F172A]" data-testid="category-add-button">{t("create")}</Button>
        </div>
      )}
      {[["trade", t("trades")], ["property", t("properties")]].map(([type, title]) => (
        <div key={type}>
          <h3 className="mb-3 font-display text-lg font-bold">{title}</h3>
          <div className="flex flex-wrap gap-2">
            {cats.all.filter((c) => c.type === type).map((c) => (
              <span key={c.id} className="flex items-center gap-2 rounded-sm border border-slate-200 bg-white px-3 py-1.5 text-sm" data-testid={`category-${c.key}`}>
                {catName(c, lang)} <span className="text-slate-400">· {c.key}</span>
                {isAdmin && <button onClick={() => del(c.id)} className="text-slate-500 hover:text-rose-600" data-testid={`category-delete-${c.key}`}><Trash2 size={12} /></button>}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const { t } = usePrefs();
  const isAdmin = user.role === "admin";
  const downloadAccess = async () => {
    try {
      const r = await api.get("/admin/access-pdf", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "gestimmo_acces.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="admin-page">
      <PageHeader eyebrow={t(`role_${user.role}`)} title={t("admin")}>
        {isAdmin && <Button variant="outline" className="rounded-sm" onClick={downloadAccess} data-testid="download-access-pdf"><FileText size={15} className="mr-1" />{t("accessPdf")}</Button>}
      </PageHeader>
      <Tabs defaultValue="users">
        <TabsList className="mb-6 rounded-sm">
          <TabsTrigger value="users" data-testid="admin-tab-users">{t("users")}</TabsTrigger>
          <TabsTrigger value="providers" data-testid="admin-tab-providers">{t("providers")}</TabsTrigger>
          <TabsTrigger value="categories" data-testid="admin-tab-categories">{t("categories")}</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="admin-tab-reviews">{t("reviewsTab")}</TabsTrigger>
          <TabsTrigger value="security" data-testid="admin-tab-security">{t("security")}</TabsTrigger>
        </TabsList>
        <TabsContent value="users"><Users isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="providers"><Providers isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="categories"><Categories isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="reviews"><ThankYouSettings isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="security"><SecurityPanel /></TabsContent>
      </Tabs>
    </div>
  );
}
